import React, { useEffect, useState } from "react";
import { Box, Card, Typography, Button, Fab, Tooltip, Zoom } from "@mui/material";
import { motion } from "framer-motion";
// import dash from "../assets/GW_BK2.png";
import gwaybk from "../assets/gwaybk.mp4";
import Statistics from "./Statistics";
import formatNumber from "../utils/formatNumber";
import TodoPanel from "./TodoPanel";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

export default function Dashboard({ totalUsers, setActiveSection, handleOpenRegistration, setStatusFilter, users = [], dashboardData }) {
  const [count, setCount] = useState(0);
  const [todoOpen, setTodoOpen] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  // Smooth Number Counter - FIXED
  useEffect(() => {
    if (totalUsers === undefined || totalUsers === null) {
      setCount(0);
      return;
    }

    const end = Number(totalUsers) || 0;

    if (end === 0) {
      setCount(0);
      return;
    }

    // Reset count to 0 when totalUsers changes
    setCount(0);

    let start = 0;
    const duration = 2000;
    const frameRate = 16;

    // Calculate increment for smoother transition
    const increment = Math.max(1, Math.ceil(end / (duration / frameRate)));

    const timer = setInterval(() => {
      start += increment;

      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, frameRate);

    return () => clearInterval(timer);
  }, [totalUsers]);

  return (
    <>
      <Box sx={{ flexGrow: 1, background: "black", minHeight: "100vh", m: 0, position: 'relative' }}>
        {/* Banner - Video/Image Transition */}
        <Box sx={{
          width: "100%",
          height: { xs: "100vh", sm: "105vh" },
          overflow: "hidden",
          marginTop: { xs: -8.75, sm: -13 },
          position: "relative",
          zIndex: 0
        }}>
            <video
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={() => setVideoEnded(true)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none"
              }}
            >
              <source src={gwaybk} type="video/mp4" />
            </video>
        </Box>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>

        {/* spacer for fixed app bar */}
        <Box sx={{ height: 60 }} />

        {/* Dashboard Overview */}
        <Box sx={{
          marginTop: { xs: 2, sm: 5, md: 15 },
          px: { xs: 2, sm: 3, md: 4 },
          width: "100%"
        }}>
          <Card sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            height: { xs: "auto", md: 320 },
            minHeight: { xs: 200, md: 280 },
            width: "100%",
            overflowX: "hidden",
            background: `
              linear-gradient(135deg, rgba(12, 15, 30, 0.95) 0%, rgba(23, 26, 45, 0.98) 100%),
              repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(56, 109, 152, 0.05) 10px, rgba(56, 109, 152, 0.05) 20px),
              repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(76, 175, 80, 0.03) 10px, rgba(76, 175, 80, 0.03) 20px)
            `,
            position: "relative",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: `
              0 10px 40px rgba(0, 0, 0, 0.3),
              0 0 0 1px rgba(56, 109, 152, 0.2),
              inset 0 0 0 1px rgba(255, 255, 255, 0.05)
            `,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 30%, rgba(56, 109, 152, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(76, 175, 80, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 80%, rgba(203, 94, 114, 0.08) 0%, transparent 50%)
              `,
              pointerEvents: "none",
              zIndex: 0,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              background: `
                linear-gradient(
                  to right,
                  transparent 20%,
                  rgba(255, 255, 255, 0.03) 50%,
                  transparent 80%
                )
              `,
              transform: "rotate(30deg)",
              animation: "shine 8s infinite linear",
              pointerEvents: "none",
              zIndex: 0,
            },
            "& .hover-circle": {
              position: "absolute",
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(56, 109, 152, 0.15) 0%, transparent 70%)",
              pointerEvents: "none",
              zIndex: 1,
              transform: "translate(-50%, -50%)",
              opacity: 1,
              filter: "blur(10px)",
              animation: "pulse 4s infinite ease-in-out",
            },
            "& .hover-circle-2": {
              position: "absolute",
              width: "150px",
              height: "150px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)",
              pointerEvents: "none",
              zIndex: 1,
              transform: "translate(-50%, -50%)",
              opacity: 0.7,
              filter: "blur(15px)",
              animation: "pulse 5s infinite ease-in-out reverse",
            },
            "& .hover-circle-3": {
              position: "absolute",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(203, 94, 114, 0.08) 0%, transparent 70%)",
              pointerEvents: "none",
              zIndex: 1,
              transform: "translate(-50%, -50%)",
              opacity: 0.5,
              filter: "blur(8px)",
              animation: "pulse 3.5s infinite ease-in-out",
            }
          }}>
            {/* Always visible animated circles */}
            <Box
              className="hover-circle"
              sx={{
                top: "20%",
                left: "85%",
                animationDelay: "0s"
              }} />
            <Box
              className="hover-circle-2"
              sx={{
                top: "70%",
                left: "15%",
                animationDelay: "1s"
              }} />
            <Box
              className="hover-circle-3"
              sx={{
                top: "40%",
                left: "40%",
                animationDelay: "0.5s"
              }} />

            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 800,
                  color: "#ffffff",
                  mb: 2,
                  position: "relative",
                  display: "inline-block",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                  textShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-10px",
                    left: 0,
                    width: 80,
                    height: 4,
                    background: "linear-gradient(90deg, #386d98 0%, #4caf50 100%)",
                    borderRadius: 2,
                    boxShadow: "0 0 10px rgba(56, 109, 152, 0.5)",
                    animation: "glowPulse 3s infinite ease-in-out"
                  }
                }}
              >
                Dashboard Overview
              </Typography>

              <Box sx={{
                fontFamily: "Poppins, sans-serif",
                color: "rgba(255, 255, 255, 0.85)",
                mb: 4,
                pt: { xs: 2, md: 4 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                maxWidth: "800px",
                lineHeight: 1.6,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                position: "relative"
              }}>
                Welcome to GateWay Insurance Admin Dashboard. Manage your customers efficiently with advanced analytics and real-time insights powered by cutting-edge technology.

                {/* Floating particles effect */}
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  display: { xs: "none", sm: "block" },
                  "& span": {
                    position: "absolute",
                    background: "rgba(255, 255, 255, 0.39)",
                    borderRadius: "50%",
                    animation: "float 8s infinite ease-in-out",
                    "&:nth-of-type(1)": { top: "20%", left: "10%", width: "4px", height: "4px", animationDelay: "0s" },
                    "&:nth-of-type(2)": { top: "40%", left: "85%", width: "3px", height: "3px", animationDelay: "1.5s" },
                    "&:nth-of-type(3)": { top: "60%", left: "30%", width: "5px", height: "5px", animationDelay: "3s" },
                    "&:nth-of-type(4)": { top: "80%", left: "70%", width: "2px", height: "2px", animationDelay: "4.5s" },
                    "&:nth-of-type(5)": { top: "10%", left: "50%", width: "4px", height: "4px", animationDelay: "6s" },
                    "&:nth-of-type(6)": { top: "15%", left: "25%", width: "3px", height: "3px", animationDelay: "1s" },
                    "&:nth-of-type(7)": { top: "35%", left: "15%", width: "2px", height: "2px", animationDelay: "2s" },
                    "&:nth-of-type(8)": { top: "45%", left: "60%", width: "4px", height: "4px", animationDelay: "3.5s" },
                    "&:nth-of-type(9)": { top: "75%", left: "20%", width: "3px", height: "3px", animationDelay: "5s" },
                    "&:nth-of-type(10)": { top: "25%", left: "90%", width: "4px", height: "4px", animationDelay: "0.5s" },
                    "&:nth-of-type(11)": { top: "55%", left: "45%", width: "2px", height: "2px", animationDelay: "2.5s" },
                    "&:nth-of-type(12)": { top: "85%", left: "10%", width: "5px", height: "5px", animationDelay: "4s" },
                    "&:nth-of-type(13)": { top: "5%", left: "75%", width: "3px", height: "3px", animationDelay: "5.5s" },
                    "&:nth-of-type(14)": { top: "95%", left: "55%", width: "2px", height: "2px", animationDelay: "7s" },
                    "&:nth-of-type(15)": { top: "50%", left: "80%", width: "4px", height: "4px", animationDelay: "1.2s" }
                  }
                }}>
                  {[...Array(15)].map((_, i) => <span key={i}></span>)}
                </Box>
              </Box>

              <Box sx={{
                display: "flex",
                gap: { xs: 2, md: 3 },
                flexWrap: "wrap",
                alignItems: "center"
              }}>
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #386d98 0%, #2a5778 100%)",
                    color: "#ffffff",
                    px: { xs: 3, md: 5 },
                    py: 1.8,
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", md: "1rem" },
                    borderRadius: 1,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    boxShadow: `
                      0 4px 15px rgba(56, 109, 152, 0.4),
                      0 0 0 1px rgba(255, 255, 255, 0.05),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1)
                    `,
                    position: "relative",
                    overflow: "hidden",
                    animation: "buttonGlow 4s infinite ease-in-out",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
                      transition: "left 0.5s ease"
                    },
                    "&:hover": {
                      background: "linear-gradient(135deg, #2a5778 0%, #386d98 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: `
                        0 8px 25px rgba(56, 109, 152, 0.6),
                        0 0 0 1px rgba(255, 255, 255, 0.1)
                      `,
                      animation: "none",
                      "&::before": {
                        left: "100%"
                      }
                    },
                    transition: "all 0.3s ease"
                  }}
                  onClick={handleOpenRegistration}
                >
                  Register New Customer
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #4caf50 0%, #3d8b40 100%)",
                    color: "#ffffff",
                    px: { xs: 3, md: 5 },
                    py: 1.8,
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", md: "1rem" },
                    borderRadius: 1,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    boxShadow: `
                      0 4px 15px rgba(76, 175, 80, 0.4),
                      0 0 0 1px rgba(255, 255, 255, 0.05),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1)
                    `,
                    position: "relative",
                    overflow: "hidden",
                    animation: "buttonGlow 4s infinite ease-in-out 0.5s",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
                      transition: "left 0.5s ease"
                    },
                    "&:hover": {
                      background: "linear-gradient(135deg, #3d8b40 0%, #4caf50 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: `
                        0 8px 25px rgba(76, 175, 80, 0.6),
                        0 0 0 1px rgba(255, 255, 255, 0.1)
                      `,
                      animation: "none",
                      "&::before": {
                        left: "100%"
                      }
                    },
                    transition: "all 0.3s ease"
                  }}
                  onClick={() => setActiveSection("LeadModule")}
                >
                  View All Customers
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>

        <style>{`
          @keyframes shine {
            0% {
              transform: rotate(30deg) translateX(-100%);
            }
            100% {
              transform: rotate(30deg) translateX(100%);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.7;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.1);
              opacity: 0.9;
            }
          }
          
          @keyframes glowPulse {
            0%, 100% {
              opacity: 0.8;
              box-shadow: 0 0 10px rgba(56, 109, 152, 0.5);
            }
            50% {
              opacity: 1;
              box-shadow: 0 0 20px rgba(56, 109, 152, 0.8);
            }
          }
          
          @keyframes float {
            0%, 100% {
              transform: translateY(0) translateX(0);
              opacity: 0.4;
            }
            25% {
              transform: translateY(-10px) translateX(5px);
              opacity: 0.7;
            }
            50% {
              transform: translateY(-5px) translateX(10px);
              opacity: 0.5;
            }
            75% {
              transform: translateY(-15px) translateX(-5px);
              opacity: 0.6;
            }
          }
          
          @keyframes buttonGlow {
            0%, 100% {
              box-shadow: 
                0 4px 15px rgba(56, 109, 152, 0.4),
                0 0 0 1px rgba(255, 255, 255, 0.05),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
            }
            50% {
              box-shadow: 
                0 4px 20px rgba(56, 109, 152, 0.6),
                0 0 0 1px rgba(255, 255, 255, 0.05),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
            }
          }

          @keyframes fabPulse {
            0% { box-shadow: 0 0 0 0 rgba(253, 184, 19, 0.7); }
            70% { box-shadow: 0 0 0 15px rgba(253, 184, 19, 0); }
            100% { box-shadow: 0 0 0 0 rgba(253, 184, 19, 0); }
          }
        `}</style>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* === SAME ROW — Content + Card === */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 4, md: 3 },
              mb: 4,
              mt: { xs: 5, md: 15 },
              alignItems: "stretch",
              flexWrap: { xs: "wrap", md: "nowrap" },
              px: { xs: 2, sm: 3, md: 4 }
            }}
          >
            {/* LEFT CONTENT — approx 60% */}
            <Box sx={{
              flex: { xs: "100%", md: "0 0 63%" },
              p: { xs: 1, md: 3 },
              order: { xs: 2, md: 1 }
            }}>
              <Typography
                variant="h5"
                sx={{
                  mt: { xs: 2, md: 6 },
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  fontSize: { xs: "1.5rem", md: "2rem" }
                }}
              >
                Insurance Services
              </Typography>

              <Typography
                sx={{
                  mt: 2.5,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "0.95rem", md: "1.2rem" },
                  lineHeight: 1.6,
                  color: "text.secondary"
                }}
              >
                Protect your future with our trusted insurance solutions.
                We provide personalized customer support and secure policy coverage
                designed to meet your financial safety needs.
              </Typography>
            </Box>

            {/* RIGHT CARD — approx 30% */}
            <Box sx={{
              flex: { xs: "100%", md: "0 0 30%" },
              order: { xs: 1, md: 2 },
              display: "flex",
              justifyContent: "center"
            }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 4,
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 350, md: 300 },
                  background: "linear-gradient(135deg, #f99c2c 0%, #ff7e5f 100%)",
                  textAlign: "center",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.02)" }
                }}
                onClick={() => {
                  setStatusFilter("all");
                  setActiveSection("LeadModule");
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 900,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    color: "#ffffff",
                    mb: 2
                  }}
                >
                  Total Customers
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 900,
                    fontSize: {
                      xs: formatNumber(count).length <= 3 ? "4rem" : formatNumber(count).length <= 5 ? "3.2rem" : "2.5rem",
                      sm: formatNumber(count).length <= 3 ? "5rem" : formatNumber(count).length <= 5 ? "4rem" : "3rem",
                      md: formatNumber(count).length <= 3 ? "7.2rem" : formatNumber(count).length <= 5 ? "5.5rem" : "4rem"
                    },
                    color: "#ffffff",
                    lineHeight: 1
                  }}
                >
                  {formatNumber(count)}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "0.9rem",
                    color: "rgba(255,255,255,0.8)",
                    mt: 1
                  }}
                >
                  Registered Users
                </Typography>
              </Card>
            </Box>
          </Box>

          {/* Statistics section */}
          <Box sx={{ mb: 4 }}>
            <Statistics
              setActiveSection={setActiveSection}
              setStatusFilter={setStatusFilter}
              dashboardData={dashboardData}
            />
          </Box>
        </motion.div>
      </Box>

      {/* FAB Button - Only show when todo panel is closed */}
      {!todoOpen && (
        <Tooltip title="Task Manager" placement="left" TransitionComponent={Zoom} arrow>
          <Fab
            color="secondary"
            onClick={() => setTodoOpen(true)}
            sx={{
              position: "fixed",
              right: 24,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1400,
              background: "linear-gradient(135deg, #FDB813 0%, #e5a100 100%)",
              color: "#111111",
              animation: "fabPulse 2s infinite",
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              "&:hover": {
                transform: "translateY(-50%) scale(1.15)",
                background: "linear-gradient(135deg, #e5a100 0%, #FDB813 100%)",
                boxShadow: "0 10px 30px rgba(253, 184, 19, 0.5)"
              }
            }}
          >
            <PlaylistAddCheckIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Tooltip>
      )}

      {/* Todo Panel */}
      <TodoPanel
        open={todoOpen}
        onClose={() => setTodoOpen(false)}
      />
    </>
  );
}